/**
 * QuickEditVehicleDialog
 * ----------------------------------------------------------------------
 * Small in-modal editor for rego / make / model / year / color / VIN /
 * odometer so SM can correct a walk-in's vehicle details from inside the
 * Job Card modal. Mirrors the larger Edit Vehicle dialog in InvoiceEdit
 * trimmed down to drop-off essentials.
 *
 * Calls PUT /api/vehicles/{id} then fires onSaved() so the parent can
 * refresh its denormalized JC snapshot.
 */
import { useEffect, useState } from 'react';
import { formatRego } from '@/lib/formatters';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const EMPTY = {
  rego: '', make: '', model: '', year: '',
  color: '', vin: '', odometer: '', fuel_type: '',
};

export default function QuickEditVehicleDialog({ open, onOpenChange, vehicleId, jobCardId, onSaved }) {
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open || !vehicleId) return;
    let active = true;
    (async () => {
      setLoading(true);
      try {
        const r = await fetch(`${BACKEND_URL}/api/vehicles/${vehicleId}`, {
          credentials: 'include',
        });
        if (!r.ok) throw new Error('Failed to load');
        const v = await r.json();
        if (!active) return;
        setForm({
          rego: v.rego || '',
          make: v.make || '',
          model: v.model || '',
          year: v.year ? String(v.year) : '',
          color: v.color || '',
          vin: v.vin || '',
          odometer: v.odometer ? String(v.odometer) : '',
          fuel_type: v.fuel_type || '',
        });
      } catch (_e) {
        toast.error('Could not load vehicle');
        onOpenChange(false);
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [open, vehicleId, onOpenChange]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async () => {
    if (!form.rego?.trim()) {
      toast.error('Rego is required');
      return;
    }
    if (!form.make?.trim()) {
      toast.error('Make is required');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        rego: form.rego.trim().toUpperCase(),
        year: form.year ? Number(form.year) : null,
        odometer: form.odometer ? Number(form.odometer) : null,
      };
      Object.keys(payload).forEach((k) => {
        if (payload[k] === '' || payload[k] === undefined) payload[k] = null;
      });
      // Pass `force_sync_jc_id` so the JC snapshot + linked invoice/quote
      // are force-synced even when they're drifted (see backend PUT
      // /vehicles/{id} for the rationale).
      const url = jobCardId
        ? `${BACKEND_URL}/api/vehicles/${vehicleId}?force_sync_jc_id=${encodeURIComponent(jobCardId)}`
        : `${BACKEND_URL}/api/vehicles/${vehicleId}`;
      const r = await fetch(url, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!r.ok) {
        const e = await r.json().catch(() => ({}));
        toast.error(e.detail || 'Save failed');
        return;
      }
      toast.success('Vehicle details saved');
      onSaved?.();
      onOpenChange(false);
    } catch (_e) {
      toast.error('Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl" data-testid="quick-edit-vehicle-dialog">
        <DialogHeader>
          <DialogTitle>Edit vehicle details</DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="py-10 text-center text-slate-500">Loading…</div>
        ) : (
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="qev-rego">Rego *</Label>
              <Input id="qev-rego" data-testid="qev-rego"
                value={form.rego}
                onChange={(e) => setForm(f => ({ ...f, rego: formatRego(e.target.value) }))}
                placeholder="1AY 3CQ" />
            </div>
            <div>
              <Label htmlFor="qev-year">Year</Label>
              <Input id="qev-year" data-testid="qev-year" type="number"
                value={form.year} onChange={set('year')}
                placeholder="2018" />
            </div>
            <div>
              <Label htmlFor="qev-make">Make *</Label>
              <Input id="qev-make" data-testid="qev-make"
                value={form.make} onChange={set('make')}
                placeholder="Toyota" />
            </div>
            <div>
              <Label htmlFor="qev-model">Model</Label>
              <Input id="qev-model" data-testid="qev-model"
                value={form.model} onChange={set('model')}
                placeholder="Camry" />
            </div>
            <div>
              <Label htmlFor="qev-color">Colour</Label>
              <Input id="qev-color" data-testid="qev-color"
                value={form.color} onChange={set('color')} />
            </div>
            <div>
              <Label htmlFor="qev-fuel">Fuel type</Label>
              <select
                id="qev-fuel"
                data-testid="qev-fuel"
                value={form.fuel_type}
                onChange={set('fuel_type')}
                className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
              >
                <option value="">—</option>
                <option value="Petrol">Petrol</option>
                <option value="Diesel">Diesel</option>
                <option value="Hybrid">Hybrid</option>
                <option value="EV">EV</option>
                <option value="LPG">LPG</option>
                <option value="Other">Other</option>
              </select>
            </div>
            <div>
              <Label htmlFor="qev-odo">Odometer (km)</Label>
              <Input id="qev-odo" data-testid="qev-odo" type="number"
                value={form.odometer} onChange={set('odometer')} />
            </div>
            <div>
              <Label htmlFor="qev-vin">VIN</Label>
              <Input id="qev-vin" data-testid="qev-vin"
                value={form.vin} onChange={set('vin')} />
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="qev-cancel">
            Cancel
          </Button>
          <Button onClick={save} disabled={saving || loading} data-testid="qev-save">
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
